import { useEffect, useState } from "react";

export default function useInitialAddress(userInfo) {
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [billingAddresses, setBillingAddresses] = useState([]);
  const [selectedShippingId, setSelectedShippingId] = useState(null);
  const [selectedBillingId, setSelectedBillingId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize addresses when userInfo becomes available
  useEffect(() => {
    if (userInfo && !isInitialized) {
      let initialShippingAddresses = [];
      let initialBillingAddresses = [];

      // Check for billing address
      if (userInfo.billing_address_name) {
        const billingAddress = {
          id: Date.now(),
          fullName: `${userInfo.firstname} ${userInfo.lastname}`,
          address:
            userInfo.billing_address_name.split("\n")[0] || "Unknown Address",
          addrText: userInfo.billing_addrtext || "",
          city: userInfo.billing_city,
          state: userInfo.billing_state,
          zip: userInfo.billing_zip,
          country: userInfo.billing_country,
          phone: userInfo.phone || "Phone not available",
          isDefault: true,
        };
        initialBillingAddresses.push(billingAddress);
      }

      // Check for shipping address
      if (userInfo.shipping_address_name) {
        const shippingAddress = {
          id: Date.now() + 1,
          fullName: `${userInfo.firstname} ${userInfo.lastname}`,
          address:
            userInfo.shipping_address_name.split("\n")[0] || "Unknown Address",
          addrText: userInfo.shipping_addrtext || "",
          city: userInfo.shipping_city,
          state: userInfo.shipping_state,
          zip: userInfo.shipping_zip,
          country: userInfo.shipping_country,
          phone: userInfo.phone || "Phone not available",
          isDefault: true,
        };
        initialShippingAddresses.push(shippingAddress);
      }

      // Check for default address if no specific addresses are available
      if (
        (initialBillingAddresses.length === 0 ||
          initialShippingAddresses.length === 0) &&
        userInfo.defaultaddress
      ) {
        const defaultAddr = userInfo.defaultaddress;
        const defaultAddress = {
          id: Date.now() + 2,
          fullName: `${userInfo.firstname} ${userInfo.lastname}`,
          address: defaultAddr.addr1 || "Unknown Address",
          city: defaultAddr.city,
          state: defaultAddr.state,
          zip: defaultAddr.zip,
          country: defaultAddr.country,
          phone: userInfo.phone || "Phone not available",
          isDefault: true,
        };

        if (initialBillingAddresses.length === 0) {
          initialBillingAddresses.push(defaultAddress);
        }
        if (initialShippingAddresses.length === 0) {
          initialShippingAddresses.push({
            ...defaultAddress,
            id: Date.now() + 3,
          });
        }
      }

      // Check for addressbook entries if no addresses found yet
      if (
        (initialBillingAddresses.length === 0 ||
          initialShippingAddresses.length === 0) &&
        userInfo.addressbook &&
        userInfo.addressbook.length > 0
      ) {
        const addressBookAddresses = userInfo.addressbook.map(
          (addr, index) => ({
            id: Date.now() + index + 4,
            fullName: `${userInfo.firstname} ${userInfo.lastname}`,
            address: addr.addr1 || "Unknown Address",
            city: addr.city,
            state: addr.state,
            zip: addr.zip,
            country: addr.country,
            phone: addr.phone || userInfo.phone || "Phone not available",
            isDefault: index === 0,
          })
        );

        if (initialBillingAddresses.length === 0) {
          initialBillingAddresses = [...addressBookAddresses];
        }
        if (initialShippingAddresses.length === 0) {
          initialShippingAddresses = addressBookAddresses.map((addr) => ({
            ...addr,
            id: addr.id + 1000, // Ensure unique IDs
          }));
        }
      }

      setBillingAddresses(initialBillingAddresses);
      setShippingAddresses(initialShippingAddresses);

      // Pre-select the default address (checkout's "Set as default" promises
      // "new orders will ship to" it), falling back to the first.
      if (initialBillingAddresses.length > 0) {
        const preferred =
          initialBillingAddresses.find((addr) => addr.isDefault) ||
          initialBillingAddresses[0];
        setSelectedBillingId(preferred.id);
      }
      if (initialShippingAddresses.length > 0) {
        const preferred =
          initialShippingAddresses.find((addr) => addr.isDefault) ||
          initialShippingAddresses[0];
        setSelectedShippingId(preferred.id);
      }

      setIsInitialized(true);
    }
  }, [userInfo, isInitialized]);

  return {
    // Shipping addresses
    shippingAddresses,
    setShippingAddresses,
    selectedShippingId,
    setSelectedShippingId,

    // Billing addresses
    billingAddresses,
    setBillingAddresses,
    selectedBillingId,
    setSelectedBillingId,
  };
}
